import './LockDeckButton.scss';

import { useAurigaContext } from '@context/AurigaContext.tsx';
import { useDeckContext } from '@context/DeckContext.tsx';
import { PermissionLevel, useUserContext } from '@context/UserContext.tsx';
import LockClock from '@mui/icons-material/LockClock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, Button, Tooltip } from '@mui/material';
import { useLockDeck, useReleaseDeck } from '@services/api/deckManagement/hooks/useDeck';
import { useLockedPagesForDeck } from '@services/api/deckManagement/hooks/usePage';
import { formatTimestampToLocal } from '@utils/dateHelpers';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Reusable Lock/Release Deck button component for side panels
 * Shows "Lock Deck" or "Release Deck" based on the current lock status
 */
interface LockDeckButtonProps {
  /** Optional callback invoked after the deck lock is successfully released. */
  onReleaseSuccess?: () => void;
}

const LockDeckButton = ({ onReleaseSuccess }: LockDeckButtonProps) => {
  const { deckId } = useParams<{ deckId: string }>();
  const { username, hasPermissionLevel } = useUserContext();
  const { deckLockStatus, setDeckLockStatus, lockAlertId, setLockAlertId, currentDeck, currentDeckId } =
    useDeckContext();
  const { toastNotification, showAlert, dismissAlert, checkDuplicateAlert } = useAurigaContext();

  // Check if user is DevTeamAdmin for break-glass functionality
  const isDevTeamAdmin = hasPermissionLevel(PermissionLevel.DEV_TEAM_ADMIN);
  // Only the Actuals deck (id: '1') can be locked by any user; all other decks require DevTeamAdmin
  const isActualsDeck = currentDeckId === '1' && currentDeck?.deck_name === 'Actuals';
  const canLockDeck = isActualsDeck || isDevTeamAdmin;

  // A deck lock cannot be acquired while any page is locked (mutual exclusion),
  // so the button is disabled and a banner lists the locked pages.
  const { data: lockedPages = [] } = useLockedPagesForDeck({
    deckId: deckId || '',
    options: { enabled: Boolean(deckId) && !deckLockStatus.isLocked },
  });
  // Only block the deck lock for locked pages when the deck itself isn't locked (once the
  // deck is locked, no page can be locked, and the button must remain usable to release).
  const hasLockedPages = !deckLockStatus.isLocked && lockedPages.length > 0;

  // Show a banner listing the locked page(s), holder, and auto-release time while any page
  // is locked (and the deck itself is not locked). Clears automatically when all page locks
  // are released (manual or auto-release) via the live-invalidated query.
  useEffect(() => {
    if (deckLockStatus.isLocked || !hasLockedPages) {
      return undefined;
    }

    const pageLines = lockedPages
      .map((lock) => {
        const holder = lock.locked_by ?? 'another user';
        const expiry = lock.lock_expiry ? formatTimestampToLocal(lock.lock_expiry, 'h:mm A') : '';
        const expiryText = expiry ? ` and will auto-release at ${expiry}` : '';
        return ` \u2022 Page ${lock.page_name} is locked by ${holder}${expiryText}`;
      })
      .join('\n');

    const alertId = showAlert({
      message: `Deck cannot be locked while these pages are locked:\n${pageLines}`,
      type: 'warning' as const,
      variant: 'standard' as const,
      visible: true,
      alertRoutes: ['/toc', '/scenario-management'],
      dismissible: false,
      icon: <LockClock />,
    });

    return () => {
      if (alertId) dismissAlert(alertId);
    };
  }, [deckLockStatus.isLocked, hasLockedPages, lockedPages, showAlert, dismissAlert]);

  // Show system alert when the deck is locked
  useEffect(() => {
    // Dismiss any previous lock if it exists and the Deck is no longer locked
    if (lockAlertId && !deckLockStatus.isLocked) {
      dismissAlert(lockAlertId);
      setLockAlertId(null);
    }

    // Only show new alert if the deck is actually locked
    if (deckLockStatus.isLocked && deckLockStatus.lockedBy && deckLockStatus.lockedAt && deckLockStatus.lockExpiry) {
      // lock_expiry is the authoritative absolute expiry timestamp (ISO-8601)
      const formattedLockedAt = formatTimestampToLocal(deckLockStatus.lockedAt, 'h:mm A');
      const formattedExpiryAt = formatTimestampToLocal(deckLockStatus.lockExpiry, 'h:mm A');

      const alertData = {
        message: `Deck locked by ${deckLockStatus.lockedBy} at ${formattedLockedAt}. The lock will be automatically released at ${formattedExpiryAt}`,
        type: 'warning' as const,
        variant: 'standard' as const,
        visible: true,
        alertRoutes: ['all'],
        dismissible: false,
        icon: <LockClock />,
      };

      // Track alert ID for dismissing when status changes
      const alertId = showAlert(alertData);
      setLockAlertId(alertId);
    }
  }, [
    deckLockStatus.isLocked,
    deckLockStatus.lockedBy,
    deckLockStatus.lockedAt,
    deckLockStatus.lockExpiry,
    showAlert,
    dismissAlert,
    setLockAlertId,
    lockAlertId,
    checkDuplicateAlert,
  ]);

  // Use deckLockStatus which is now properly initialized from currentDeck
  const isLockedByCurrentUser = deckLockStatus.isLocked && deckLockStatus.lockedBy === username;
  const isLockedByOtherUser = deckLockStatus.isLocked && !isLockedByCurrentUser;
  const canBreakGlass = isDevTeamAdmin && isLockedByOtherUser;

  // Lock/Release deck mutations
  const lockDeckMutation = useLockDeck({
    onSuccess: (data) => {
      const lockExpiry = data.lock_expiry ?? undefined;
      const lockedAt = data.locked_at ?? undefined;
      const lockedAtTimestamp = lockedAt ? formatTimestampToLocal(lockedAt, 'M/D/YYYY, h:mm:ss A') : '';

      setDeckLockStatus({
        isLocked: true,
        lockedBy: username,
        lockedAt,
        lockExpiry,
      });
      toastNotification({ message: `Deck Locked: ${lockedAtTimestamp}`, type: 'success', visible: true });
    },
    onError: (error) => {
      toastNotification({ message: `Failed to lock deck: ${error.message}`, type: 'error', visible: true });
    },
  });

  const releaseDeckMutation = useReleaseDeck({
    onSuccess: () => {
      setDeckLockStatus({
        isLocked: false,
        lockedBy: undefined,
        lockedAt: undefined,
        isLockedByCurrentUser: false,
        isEditingDisabled: false,
      });
      toastNotification({ message: 'Deck released successfully', type: 'success', visible: true });
      onReleaseSuccess?.();
    },
    onError: (error) => {
      toastNotification({ message: `Failed to release deck: ${error.message}`, type: 'error', visible: true });
    },
  });

  const handleClick = () => {
    // Allow DevTeamAdmin to break-glass unlock any deck
    if (isLockedByOtherUser && !canBreakGlass) {
      return; // Do nothing if locked by another user and not DevTeamAdmin
    }

    if (deckLockStatus.isLocked && (isLockedByCurrentUser || canBreakGlass)) {
      releaseDeckMutation.mutate({ deckId: deckId! });
    } else if (!deckLockStatus.isLocked) {
      lockDeckMutation.mutate({ deckId: deckId! });
    }
  };

  const isLoading = lockDeckMutation.isPending || releaseDeckMutation.isPending;

  let tooltipText = 'Lock deck to publish or edit Page Library & Scenario Management';
  if (!canLockDeck) {
    tooltipText = 'Only Auriga Dev Team admins can lock this deck';
  } else if (hasLockedPages) {
    tooltipText = 'Deck cannot be locked while one or more of its pages are locked';
  } else if (isLockedByOtherUser && canBreakGlass) {
    tooltipText = `Break-glass unlock (Admin override) - Locked by ${deckLockStatus.lockedBy}`;
  } else if (isLockedByOtherUser) {
    tooltipText = `Locked by ${deckLockStatus.lockedBy}`;
  } else if (deckLockStatus.isLocked) {
    tooltipText = 'Release deck lock';
  }

  let lockButtonText = 'Lock Deck';
  if (isLoading) {
    lockButtonText = deckLockStatus.isLocked ? 'Releasing...' : 'Locking...';
  } else if (deckLockStatus.isLocked) {
    lockButtonText = 'Release Deck';
  }

  return (
    <Tooltip title={tooltipText} arrow>
      <Box className='lock-deck-button'>
        <Button
          variant={deckLockStatus.isLocked ? 'outlined' : 'contained'}
          color='secondary'
          size='medium'
          startIcon={deckLockStatus.isLocked ? <LockOpenIcon /> : <LockClock />}
          disabled={isLoading || !canLockDeck || hasLockedPages || (isLockedByOtherUser && !canBreakGlass)}
          onClick={handleClick}
          fullWidth
        >
          {lockButtonText}
        </Button>
      </Box>
    </Tooltip>
  );
};

export default LockDeckButton;
